import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CircleNotch, Sparkle, Plus, Trash, PencilSimple, CheckCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const whenUsed = (value) => value ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Not used yet";

export function CategorizationRulesPanel() {
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [rulesPayload, setRulesPayload] = useState({ rowan_intro: "", rules: [] });
  const [cats, setCats] = useState({ income: [], expense: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState({ vendor_name: "", category: "", txn_type: "expense" });
  const [editForm, setEditForm] = useState({ vendor_name: "", category: "", txn_type: "expense" });

  const loadBusinesses = async () => {
    const { data } = await api.get("/businesses");
    setBusinesses(data || []);
    if (!selectedBusinessId && data?.[0]?.id) {
      setSelectedBusinessId(data[0].id);
    }
  };

  const loadRules = async (businessId) => {
    if (!businessId) return;
    setLoading(true);
    try {
      const [rulesRes, catsRes] = await Promise.all([
        api.get(`/businesses/${businessId}/categorization-rules`),
        api.get(`/businesses/${businessId}/categories`),
      ]);
      setRulesPayload(rulesRes.data);
      setCats(catsRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load categorization rules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusinesses().catch((err) => {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load businesses.");
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedBusinessId) loadRules(selectedBusinessId);
  }, [selectedBusinessId]);

  const categoryOptions = useMemo(() => cats[form.txn_type] || [], [cats, form.txn_type]);
  const editCategoryOptions = useMemo(() => cats[editForm.txn_type] || [], [cats, editForm.txn_type]);

  const addRule = async () => {
    if (!form.vendor_name.trim()) return toast.error("Add a vendor name first.");
    if (!form.category) return toast.error("Pick a category for the rule.");
    setSaving(true);
    try {
      await api.post(`/businesses/${selectedBusinessId}/categorization-rules`, form);
      toast.success("Rule saved. Rowan will use it on the next matching transaction.");
      setForm({ vendor_name: "", category: "", txn_type: "expense" });
      setShowForm(false);
      await loadRules(selectedBusinessId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save that rule.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setEditForm({ vendor_name: rule.vendor_name, category: rule.category, txn_type: rule.txn_type || "expense" });
  };

  const saveEdit = async () => {
    if (!editForm.vendor_name.trim()) return toast.error("Vendor name can't be empty.");
    if (!editForm.category) return toast.error("Pick a category first.");
    setSaving(true);
    try {
      await api.patch(`/categorization-rules/${editingId}`, editForm);
      toast.success("Rule updated.");
      setEditingId("");
      await loadRules(selectedBusinessId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update that rule.");
    } finally {
      setSaving(false);
    }
  };

  const removeRule = async (ruleId) => {
    setDeletingId(ruleId);
    try {
      await api.delete(`/categorization-rules/${ruleId}`);
      toast.success("Rule deleted.");
      await loadRules(selectedBusinessId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't delete that rule.");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <section className="bk-card p-6 mt-8" data-testid="settings-categorization-rules-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2">
            <Sparkle size={18} weight="fill" color="var(--bk-gold)" />
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Categorization Rules</p>
          </div>
          <h2 className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>Teach Rowan once. Let Rowan remember forever.</h2>
          <p className="text-sm mt-3" style={{ color: "var(--bk-text-2)" }} data-testid="settings-categorization-rules-rowan-intro">{rulesPayload.rowan_intro || "These are the rules I've learned from you. The more you teach me, the less you have to do."}</p>
          <Link to="/receipt-rules" data-testid="settings-open-receipt-rules-manager" className="inline-flex items-center gap-1 text-sm font-semibold mt-2 underline-offset-4 hover:underline" style={{ color: "var(--bk-accent)" }}>Open the Receipt Rules Manager — every learned rule across your businesses →</Link>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select value={selectedBusinessId} onChange={(e) => setSelectedBusinessId(e.target.value)} data-testid="settings-categorization-rules-business-select" className="rounded-full border px-4 py-2.5 text-sm bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
            {businesses.map((business) => <option key={business.id} value={business.id}>{business.name}</option>)}
          </select>
          <button onClick={() => setShowForm((s) => !s)} data-testid="settings-categorization-rules-add-button" className="bk-btn-primary px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
            <Plus size={15} weight="bold" /> Add rule manually
          </button>
        </div>
      </div>

      {showForm && (
        <div className="mt-6 rounded-[24px] border p-5 bg-white/90" style={{ borderColor: "var(--bk-border)" }} data-testid="settings-categorization-rules-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input value={form.vendor_name} onChange={(e) => setForm((s) => ({ ...s, vendor_name: e.target.value }))} data-testid="settings-categorization-rules-vendor-input" placeholder="Vendor name (e.g. Home Depot)" className="rounded-md border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <select value={form.txn_type} onChange={(e) => setForm((s) => ({ ...s, txn_type: e.target.value, category: "" }))} data-testid="settings-categorization-rules-type-select" className="rounded-md border px-3 py-2.5 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
              <option value="expense">Expense</option>
              <option value="income">Income</option>
            </select>
            <select value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} data-testid="settings-categorization-rules-category-select" className="rounded-md border px-3 py-2.5 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
              <option value="">Pick a category</option>
              {categoryOptions.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap justify-end gap-2 mt-4">
            <button onClick={() => setShowForm(false)} data-testid="settings-categorization-rules-cancel-button" className="px-4 py-2 rounded-full border text-sm font-semibold" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>Cancel</button>
            <button onClick={addRule} disabled={saving} data-testid="settings-categorization-rules-save-button" className="bk-btn-primary px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
              {saving ? <><CircleNotch size={14} className="animate-spin" /> Saving…</> : <><CheckCircle size={14} weight="fill" /> Save rule</>}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-14 flex items-center justify-center gap-2 text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="settings-categorization-rules-loading">
          <CircleNotch size={18} className="animate-spin" /> Loading Rowan's learned rules…
        </div>
      ) : rulesPayload.rules?.length ? (
        <div className="mt-6 overflow-x-auto" data-testid="settings-categorization-rules-table-wrap">
          <table className="min-w-full text-sm" data-testid="settings-categorization-rules-table">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}>
                <th className="py-3 pr-4 font-semibold">Vendor</th>
                <th className="py-3 pr-4 font-semibold">Category</th>
                <th className="py-3 pr-4 font-semibold">Times Applied</th>
                <th className="py-3 pr-4 font-semibold">Last Used</th>
                <th className="py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rulesPayload.rules.map((rule) => {
                const editing = editingId === rule.id;
                return (
                  <tr key={rule.id} className="border-b last:border-b-0" style={{ borderColor: "var(--bk-border)" }} data-testid={`settings-categorization-rule-row-${rule.id}`}>
                    <td className="py-4 pr-4 align-top">
                      {editing ? <input value={editForm.vendor_name} onChange={(e) => setEditForm((s) => ({ ...s, vendor_name: e.target.value }))} data-testid={`settings-categorization-rule-vendor-${rule.id}`} className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} /> : <div>
                        <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{rule.vendor_name}</p>
                        <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{rule.txn_type === "income" ? "Income rule" : "Expense rule"} · {rule.auto_apply_silently ? "auto-apply" : "still learning"}</p>
                      </div>}
                    </td>
                    <td className="py-4 pr-4 align-top">
                      {editing ? (
                        <div className="space-y-2">
                          <select value={editForm.txn_type} onChange={(e) => setEditForm((s) => ({ ...s, txn_type: e.target.value, category: "" }))} data-testid={`settings-categorization-rule-type-${rule.id}`} className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
                            <option value="expense">Expense</option>
                            <option value="income">Income</option>
                          </select>
                          <select value={editForm.category} onChange={(e) => setEditForm((s) => ({ ...s, category: e.target.value }))} data-testid={`settings-categorization-rule-category-${rule.id}`} className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
                            <option value="">Pick a category</option>
                            {editCategoryOptions.map((category) => <option key={category} value={category}>{category}</option>)}
                          </select>
                        </div>
                      ) : (
                        <span className="inline-flex px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }}>{rule.category}</span>
                      )}
                    </td>
                    <td className="py-4 pr-4 align-top" style={{ color: "var(--bk-text)" }} data-testid={`settings-categorization-rule-times-${rule.id}`}>{rule.times_applied}</td>
                    <td className="py-4 pr-4 align-top" style={{ color: "var(--bk-text-2)" }} data-testid={`settings-categorization-rule-last-used-${rule.id}`}>{whenUsed(rule.last_used_at)}</td>
                    <td className="py-4 align-top">
                      <div className="flex justify-end gap-2">
                        {editing ? (
                          <>
                            <button onClick={() => setEditingId("")} data-testid={`settings-categorization-rule-cancel-${rule.id}`} className="px-3 py-2 rounded-full border text-xs font-semibold" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>Cancel</button>
                            <button onClick={saveEdit} disabled={saving} data-testid={`settings-categorization-rule-save-${rule.id}`} className="bk-btn-primary px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
                              {saving ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={12} weight="fill" />} Save
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(rule)} data-testid={`settings-categorization-rule-edit-${rule.id}`} className="px-3 py-2 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}><PencilSimple size={12} weight="bold" /> Edit</button>
                            <button onClick={() => removeRule(rule.id)} disabled={deletingId === rule.id} data-testid={`settings-categorization-rule-delete-${rule.id}`} className="px-3 py-2 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" style={{ borderColor: "rgba(178,59,31,0.2)", color: "#B23B1F" }}>
                              {deletingId === rule.id ? <CircleNotch size={12} className="animate-spin" /> : <Trash size={12} weight="bold" />} Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-6 rounded-[24px] border p-8 text-center" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="settings-categorization-rules-empty-state">
          <p className="font-semibold" style={{ color: "var(--bk-text)" }}>No learned rules yet.</p>
          <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>Change a transaction category a few times, or add a rule manually here for vendors you already know.</p>
        </div>
      )}
    </section>
  );
}